import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Tab,
  Tabs,
  Card,
  CardContent,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  MenuItem,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import {
  useDashboardReport,
  usePortfolioAnalysis,
  useLoanPerformance,
  useDailyCollections,
  useLoanPortfolioReport,
  useExportLoanPortfolio,
} from '../../hooks/reports/useReports';
import { useLoans, useLoanProducts } from '../../hooks/loans/useLoans';
import { LoanStatus } from '../../api/types/loan.types';
import {
  LoanPortfolioQueryParams,
  LoanPortfolioStatus,
  LoanPortfolioClassification,
} from '../../api/types/report.types';
import { format, differenceInDays } from 'date-fns';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const Reports: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);

  // Date range states
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(1); // First day of current month
    return format(date, 'yyyy-MM-dd');
  });
  const [endDate, setEndDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [dailyDate, setDailyDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(num);
  };

  const formatPercentage = (value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return `${num.toFixed(2)}%`;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Reports & Analytics
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View comprehensive reports and export data
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Dashboard Summary" />
          <Tab label="Loan Portfolio" />
          <Tab label="Portfolio Analysis" />
          <Tab label="Loan Performance" />
          <Tab label="Daily Collections" />
          <Tab label="Loan Arrears Report" />
        </Tabs>
      </Box>

      {/* Dashboard Summary */}
      <TabPanel value={activeTab} index={0}>
        <DashboardSummaryTab
          formatCurrency={formatCurrency}
          formatPercentage={formatPercentage}
        />
      </TabPanel>

      {/* Loan Portfolio Report */}
      <TabPanel value={activeTab} index={1}>
        <LoanPortfolioReportTab
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          formatCurrency={formatCurrency}
        />
      </TabPanel>

      {/* Portfolio Analysis */}
      <TabPanel value={activeTab} index={2}>
        <PortfolioAnalysisTab
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          formatCurrency={formatCurrency}
          formatPercentage={formatPercentage}
        />
      </TabPanel>

      {/* Loan Performance */}
      <TabPanel value={activeTab} index={3}>
        <LoanPerformanceTab
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          formatCurrency={formatCurrency}
          formatPercentage={formatPercentage}
        />
      </TabPanel>

      {/* Daily Collections */}
      <TabPanel value={activeTab} index={4}>
        <DailyCollectionsTab
          date={dailyDate}
          onDateChange={setDailyDate}
          formatCurrency={formatCurrency}
        />
      </TabPanel>

      {/* Loan Arrears Report */}
      <TabPanel value={activeTab} index={5}>
        <LoanArrearsReportTab
          formatCurrency={formatCurrency}
        />
      </TabPanel>
    </Box>
  );
};

// Dashboard Summary Tab Component
function DashboardSummaryTab({ formatCurrency, formatPercentage }: any) {
  const { data, isLoading, error, refetch, isError } = useDashboardReport();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    const isNetworkError = errorMessage.toLowerCase().includes('network') || errorMessage.toLowerCase().includes('timeout');
    const isAuthError = errorMessage.toLowerCase().includes('401') || errorMessage.toLowerCase().includes('unauthorized');

    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load dashboard report. {errorMessage}
        </Alert>
        {isNetworkError && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Please check your internet connection and ensure the API server is accessible.
          </Alert>
        )}
        {isAuthError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your session may have expired. Please try logging in again.
          </Alert>
        )}
        <Button startIcon={<RefreshIcon />} onClick={() => refetch()} variant="outlined">
          Try Again
        </Button>
      </Box>
    );
  }

  // Check if we have valid data (flat structure from backend)
  if (!data || !data.success) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          No dashboard data available. Please try refreshing.
        </Alert>
        <Button startIcon={<RefreshIcon />} onClick={() => refetch()} variant="outlined">
          Refresh Data
        </Button>
      </Box>
    );
  }

  // Use flat structure directly from API response
  const customerMetrics = {
    totalCustomers: data.totalCustomers || 0,
    activeCustomers: data.activeCustomers || 0,
    blockedCustomers: data.blockedCustomers || 0,
  };
  const loanMetrics = {
    totalActiveLoans: data.totalActiveLoans || 0,
    totalDisbursed: data.totalDisbursed || '0',
    totalOutstanding: data.totalOutstanding || '0',
    overdueLoans: data.overdueLoans || 0,
    totalOverdueAmount: data.totalOverdueAmount || '0',
  };
  const portfolioQuality = {
    par30: data.par30 || '0',
    npl: data.npl || '0',
    collectionRate: data.collectionRate || '0',
  };
  const revenueMetrics = {
    totalRevenue: data.totalRevenue || '0',
    interestIncome: data.interestIncome || '0',
    processingFeeIncome: data.processingFeeIncome || '0',
    penaltyIncome: data.penaltyIncome || '0',
  };
  const applicationMetrics = {
    totalApplications: data.totalApplications || 0,
    pendingApplications: data.pendingApplications || 0,
    approvedApplications: data.approvedApplications || 0,
    rejectedApplications: data.rejectedApplications || 0,
  };
  const collectionMetrics = {
    totalCollected: data.totalCollected || '0',
    collectedToday: data.collectedToday || '0',
    collectedThisWeek: data.collectedThisWeek || '0',
    collectedThisMonth: data.collectedThisMonth || '0',
  };

  return (
    <Box>
      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, justifyContent: 'flex-end' }}>
        <Button startIcon={<RefreshIcon />} onClick={() => refetch()}>
          Refresh
        </Button>
      </Box>

      {/* Metrics Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2, mb: 3 }}>
        <MetricCard
          title="Total Customers"
          value={customerMetrics.totalCustomers}
          subtitle={`${customerMetrics.activeCustomers} active`}
          color="primary"
        />
        <MetricCard
          title="Active Loans"
          value={loanMetrics.totalActiveLoans}
          subtitle={formatCurrency(loanMetrics.totalDisbursed)}
          color="info"
        />
        <MetricCard
          title="Portfolio at Risk (PAR30)"
          value={formatPercentage(portfolioQuality.par30)}
          subtitle={parseFloat(portfolioQuality.par30 || '0') < 10 ? 'Good' : 'Needs Attention'}
          color={parseFloat(portfolioQuality.par30 || '0') < 10 ? 'success' : 'warning'}
        />
        <MetricCard
          title="Total Revenue"
          value={formatCurrency(revenueMetrics.totalRevenue)}
          subtitle="All time"
          color="success"
        />
      </Box>

      {/* Detailed Sections */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        {/* Applications */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Applications
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <MetricRow label="Total" value={applicationMetrics.totalApplications} />
              <MetricRow label="Pending" value={applicationMetrics.pendingApplications} />
              <MetricRow label="Approved" value={applicationMetrics.approvedApplications} />
              <MetricRow label="Rejected" value={applicationMetrics.rejectedApplications} />
            </Box>
          </CardContent>
        </Card>

        {/* Collections */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Collections
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <MetricRow label="Today" value={formatCurrency(collectionMetrics.collectedToday)} />
              <MetricRow label="This Week" value={formatCurrency(collectionMetrics.collectedThisWeek)} />
              <MetricRow label="This Month" value={formatCurrency(collectionMetrics.collectedThisMonth)} />
              <MetricRow label="Total" value={formatCurrency(collectionMetrics.totalCollected)} />
            </Box>
          </CardContent>
        </Card>

        {/* Portfolio Quality */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Portfolio Quality
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <MetricRow label="PAR 30" value={formatPercentage(portfolioQuality.par30)} />
              <MetricRow label="NPL Rate" value={formatPercentage(portfolioQuality.npl)} />
              <MetricRow label="Collection Rate" value={formatPercentage(portfolioQuality.collectionRate)} />
            </Box>
          </CardContent>
        </Card>

        {/* Revenue Breakdown */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Revenue Breakdown
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <MetricRow label="Interest Income" value={formatCurrency(revenueMetrics.interestIncome)} />
              <MetricRow label="Processing Fees" value={formatCurrency(revenueMetrics.processingFeeIncome)} />
              <MetricRow label="Penalties" value={formatCurrency(revenueMetrics.penaltyIncome)} />
              <MetricRow label="Total Revenue" value={formatCurrency(revenueMetrics.totalRevenue)} bold />
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

// Portfolio Analysis Tab Component
function PortfolioAnalysisTab({ startDate, endDate, onStartDateChange, onEndDateChange, formatCurrency, formatPercentage }: any) {
  const { data, isLoading, error, refetch } = usePortfolioAnalysis({ startDate, endDate });

  return (
    <Box>
      {/* Date Range Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => onStartDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => onEndDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <Button startIcon={<RefreshIcon />} onClick={() => refetch()} size="small">
              Refresh
            </Button>
          </Box>
        </CardContent>
      </Card>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error">
          Failed to load portfolio analysis. {error instanceof Error ? error.message : 'Please try again.'}
        </Alert>
      )}

      {data && data.analysis && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          {/* Portfolio Size */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Portfolio Size
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <MetricRow label="Total Loans" value={data.analysis.portfolioSize.totalLoans} />
                <MetricRow label="Total Disbursed" value={formatCurrency(data.analysis.portfolioSize.totalDisbursed)} />
                <MetricRow label="Average Loan Size" value={formatCurrency(data.analysis.portfolioSize.averageLoanSize)} />
              </Box>
            </CardContent>
          </Card>

          {/* Repayment Performance */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Repayment Performance
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <MetricRow label="Total Repaid" value={formatCurrency(data.analysis.repaymentPerformance.totalRepaid)} />
                <MetricRow label="On-Time Repayments" value={data.analysis.repaymentPerformance.onTimeRepayments} />
                <MetricRow label="Late Repayments" value={data.analysis.repaymentPerformance.lateRepayments} />
              </Box>
            </CardContent>
          </Card>

          {/* Portfolio at Risk */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Portfolio at Risk
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <MetricRow label="PAR 1 Day" value={formatPercentage(data.analysis.portfolioAtRisk.par1)} />
                <MetricRow label="PAR 7 Days" value={formatPercentage(data.analysis.portfolioAtRisk.par7)} />
                <MetricRow label="PAR 30 Days" value={formatPercentage(data.analysis.portfolioAtRisk.par30)} />
              </Box>
            </CardContent>
          </Card>

          {/* Loan Status Breakdown */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Loan Status Breakdown
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <MetricRow label="Active Loans" value={data.analysis.loanStatusBreakdown.activeLoans} />
                <MetricRow label="Paid Loans" value={data.analysis.loanStatusBreakdown.paidLoans} />
                <MetricRow label="Defaulted Loans" value={data.analysis.loanStatusBreakdown.defaultedLoans} />
              </Box>
            </CardContent>
          </Card>

          {/* Customer Behavior */}
          <Card sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Customer Behavior
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <MetricRow label="Repeat Borrowers" value={data.analysis.customerBehavior.repeatBorrowers} />
                <MetricRow label="First-Time Borrowers" value={data.analysis.customerBehavior.firstTimeBorrowers} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
}

// Loan Performance Tab Component
function LoanPerformanceTab({ startDate, endDate, onStartDateChange, onEndDateChange, formatCurrency, formatPercentage }: any) {
  const { data, isLoading, error, refetch } = useLoanPerformance({ startDate, endDate });

  return (
    <Box>
      {/* Date Range Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => onStartDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => onEndDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <Button startIcon={<RefreshIcon />} onClick={() => refetch()} size="small">
              Refresh
            </Button>
          </Box>
        </CardContent>
      </Card>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error">
          Failed to load loan performance. {error instanceof Error ? error.message : 'Please try again.'}
        </Alert>
      )}

      {data && data.report && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          {/* Disbursement Performance */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Disbursement Performance
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <MetricRow label="Total Disbursed" value={formatCurrency(data.report.disbursementPerformance.totalDisbursed)} />
                <MetricRow label="Number of Loans" value={data.report.disbursementPerformance.numberOfLoans} />
                <MetricRow label="Average Loan Size" value={formatCurrency(data.report.disbursementPerformance.averageLoanSize)} />
              </Box>
            </CardContent>
          </Card>

          {/* Repayment Performance */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Repayment Performance
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <MetricRow label="Total Repaid" value={formatCurrency(data.report.repaymentPerformance.totalRepaid)} />
                <MetricRow label="Fully Paid Loans" value={data.report.repaymentPerformance.fullyPaidLoans} />
                <MetricRow label="Partially Paid Loans" value={data.report.repaymentPerformance.partiallyPaidLoans} />
              </Box>
            </CardContent>
          </Card>

          {/* Default Analysis */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Default Analysis
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <MetricRow label="Defaulted Loans" value={data.report.defaultAnalysis.defaultedLoans} />
                <MetricRow label="Default Rate" value={formatPercentage(data.report.defaultAnalysis.defaultRate)} />
              </Box>
            </CardContent>
          </Card>

          {/* Profitability */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Profitability
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <MetricRow label="Interest Earned" value={formatCurrency(data.report.profitability.interestEarned)} />
                <MetricRow label="Fees Earned" value={formatCurrency(data.report.profitability.feesEarned)} />
                <MetricRow label="Penalties Earned" value={formatCurrency(data.report.profitability.penaltiesEarned)} />
                <MetricRow label="Total Earned" value={formatCurrency(data.report.profitability.totalEarned)} bold />
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
}

// Daily Collections Tab Component
function DailyCollectionsTab({ date, onDateChange, formatCurrency }: any) {
  const { data, isLoading, error, refetch } = useDailyCollections({ date });

  return (
    <Box>
      {/* Date Control */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="Report Date"
              type="date"
              value={date}
              onChange={(e) => onDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <Button startIcon={<RefreshIcon />} onClick={() => refetch()} size="small">
              Refresh
            </Button>
          </Box>
        </CardContent>
      </Card>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error">
          Failed to load daily collections. {error instanceof Error ? error.message : 'Please try again.'}
        </Alert>
      )}

      {data && data.report && (
        <>
          {/* Summary Cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Collections Summary
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <MetricRow label="Total Collected" value={formatCurrency(data.report.collectionsSummary.totalCollected)} />
                  <MetricRow label="Number of Payments" value={data.report.collectionsSummary.numberOfPayments} />
                  <MetricRow label="Average Payment" value={formatCurrency(data.report.collectionsSummary.averagePaymentSize)} />
                </Box>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Due Today
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <MetricRow label="Loans Due Today" value={data.report.dueToday.loansDueToday} />
                  <MetricRow label="Amount Due Today" value={formatCurrency(data.report.dueToday.amountDueToday)} />
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Overdue Breakdown */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Overdue Breakdown
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    1-7 Days Overdue
                  </Typography>
                  <Typography variant="h6">{data.report.overdueBreakdown.overdue1_7Days.count} loans</Typography>
                  <Typography variant="body2">{formatCurrency(data.report.overdueBreakdown.overdue1_7Days.amount)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    8-29 Days Overdue
                  </Typography>
                  <Typography variant="h6">{data.report.overdueBreakdown.overdue8_29Days.count} loans</Typography>
                  <Typography variant="body2">{formatCurrency(data.report.overdueBreakdown.overdue8_29Days.amount)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    30+ Days Overdue
                  </Typography>
                  <Typography variant="h6">{data.report.overdueBreakdown.overdue30PlusDays.count} loans</Typography>
                  <Typography variant="body2" color="error">{formatCurrency(data.report.overdueBreakdown.overdue30PlusDays.amount)}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Payment Details Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payment Details
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Loan Number</strong></TableCell>
                      <TableCell><strong>Customer</strong></TableCell>
                      <TableCell><strong>Amount</strong></TableCell>
                      <TableCell><strong>Method</strong></TableCell>
                      <TableCell><strong>Reference</strong></TableCell>
                      <TableCell><strong>Time</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.report.paymentDetails.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="text.secondary">No payments recorded for this date</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.report.paymentDetails.map((payment) => (
                        <TableRow key={payment.repaymentId}>
                          <TableCell>{payment.loanNumber}</TableCell>
                          <TableCell>{payment.customerName}</TableCell>
                          <TableCell>{formatCurrency(payment.amount)}</TableCell>
                          <TableCell>
                            <Chip label={payment.paymentMethod} size="small" />
                          </TableCell>
                          <TableCell>{payment.paymentReference}</TableCell>
                          <TableCell>{format(new Date(payment.paymentDate), 'HH:mm')}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}

// Loan Arrears Report Tab Component
type ArrearsClassification = 'Performing' | 'Watch' | 'Substandard' | 'Doubtful' | 'Loss';

interface ArrearsClassificationInfo {
  label: ArrearsClassification;
  color: 'success' | 'info' | 'warning' | 'error' | 'default';
  range: string;
}

function getArrearsClassification(daysInArrears: number): ArrearsClassificationInfo {
  if (daysInArrears <= 0) {
    return { label: 'Performing', color: 'success', range: '0 days' };
  } else if (daysInArrears <= 89) {
    return { label: 'Watch', color: 'info', range: '1-89 days' };
  } else if (daysInArrears <= 180) {
    return { label: 'Substandard', color: 'warning', range: '90-180 days' };
  } else if (daysInArrears <= 365) {
    return { label: 'Doubtful', color: 'error', range: '181-365 days' };
  } else {
    return { label: 'Loss', color: 'default', range: '365+ days' };
  }
}

function LoanArrearsReportTab({ formatCurrency }: any) {
  const [reportDate, setReportDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [classificationFilter, setClassificationFilter] = useState<ArrearsClassification | 'All'>('All');

  // Fetch all active and overdue loans
  const { data: activeLoans = [], isLoading: isLoadingActive, refetch: refetchActive } = useLoans({ status: LoanStatus.ACTIVE });
  const { data: overdueLoans = [], isLoading: isLoadingOverdue, refetch: refetchOverdue } = useLoans({ status: LoanStatus.OVERDUE });

  const isLoading = isLoadingActive || isLoadingOverdue;

  const handleRefetch = () => {
    refetchActive();
    refetchOverdue();
  };

  // Combine and process loans with arrears information
  const loansWithArrears = useMemo(() => {
    const allLoans = [...activeLoans, ...overdueLoans];
    const reportDateObj = new Date(reportDate);

    return allLoans.map(loan => {
      const dueDate = new Date(loan.dueDate);
      // Calculate days in arrears from due date to report date
      const daysInArrears = Math.max(0, differenceInDays(reportDateObj, dueDate));
      const classification = getArrearsClassification(daysInArrears);

      return {
        ...loan,
        daysInArrears,
        classification,
      };
    }).sort((a, b) => b.daysInArrears - a.daysInArrears); // Sort by days in arrears descending
  }, [activeLoans, overdueLoans, reportDate]);

  // Filter by classification
  const filteredLoans = useMemo(() => {
    if (classificationFilter === 'All') return loansWithArrears;
    return loansWithArrears.filter(loan => loan.classification.label === classificationFilter);
  }, [loansWithArrears, classificationFilter]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const stats = {
      performing: { count: 0, amount: 0 },
      watch: { count: 0, amount: 0 },
      substandard: { count: 0, amount: 0 },
      doubtful: { count: 0, amount: 0 },
      loss: { count: 0, amount: 0 },
      total: { count: 0, amount: 0 },
    };

    loansWithArrears.forEach(loan => {
      const amount = loan.amountOutstanding;
      stats.total.count++;
      stats.total.amount += amount;

      switch (loan.classification.label) {
        case 'Performing':
          stats.performing.count++;
          stats.performing.amount += amount;
          break;
        case 'Watch':
          stats.watch.count++;
          stats.watch.amount += amount;
          break;
        case 'Substandard':
          stats.substandard.count++;
          stats.substandard.amount += amount;
          break;
        case 'Doubtful':
          stats.doubtful.count++;
          stats.doubtful.amount += amount;
          break;
        case 'Loss':
          stats.loss.count++;
          stats.loss.amount += amount;
          break;
      }
    });

    return stats;
  }, [loansWithArrears]);

  return (
    <Box>
      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="Report Date"
              type="date"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="Classification Filter"
              select
              value={classificationFilter}
              onChange={(e) => setClassificationFilter(e.target.value as ArrearsClassification | 'All')}
              size="small"
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="All">All Classifications</MenuItem>
              <MenuItem value="Performing">Performing (0 days)</MenuItem>
              <MenuItem value="Watch">Watch (1-89 days)</MenuItem>
              <MenuItem value="Substandard">Substandard (90-180 days)</MenuItem>
              <MenuItem value="Doubtful">Doubtful (181-365 days)</MenuItem>
              <MenuItem value="Loss">Loss (365+ days)</MenuItem>
            </TextField>
            <Button startIcon={<RefreshIcon />} onClick={handleRefetch} size="small">
              Refresh
            </Button>
          </Box>
        </CardContent>
      </Card>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && (
        <>
          {/* Classification Summary Cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2, mb: 3 }}>
            <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="caption">Performing (0 days)</Typography>
                <Typography variant="h6">{summaryStats.performing.count} loans</Typography>
                <Typography variant="body2">{formatCurrency(summaryStats.performing.amount)}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="caption">Watch (1-89 days)</Typography>
                <Typography variant="h6">{summaryStats.watch.count} loans</Typography>
                <Typography variant="body2">{formatCurrency(summaryStats.watch.amount)}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="caption">Substandard (90-180 days)</Typography>
                <Typography variant="h6">{summaryStats.substandard.count} loans</Typography>
                <Typography variant="body2">{formatCurrency(summaryStats.substandard.amount)}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="caption">Doubtful (181-365 days)</Typography>
                <Typography variant="h6">{summaryStats.doubtful.count} loans</Typography>
                <Typography variant="body2">{formatCurrency(summaryStats.doubtful.amount)}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ bgcolor: 'grey.400' }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="caption">Loss (365+ days)</Typography>
                <Typography variant="h6">{summaryStats.loss.count} loans</Typography>
                <Typography variant="body2">{formatCurrency(summaryStats.loss.amount)}</Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Loans Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Loan Arrears Details ({filteredLoans.length} loans)
              </Typography>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Loan Number</strong></TableCell>
                      <TableCell><strong>Customer Name</strong></TableCell>
                      <TableCell><strong>Location</strong></TableCell>
                      <TableCell><strong>Contact</strong></TableCell>
                      <TableCell><strong>Contact Person</strong></TableCell>
                      <TableCell><strong>Loan Officer</strong></TableCell>
                      <TableCell align="right"><strong>Principal</strong></TableCell>
                      <TableCell align="right"><strong>Outstanding</strong></TableCell>
                      <TableCell><strong>Due Date</strong></TableCell>
                      <TableCell align="right"><strong>Days in Arrears</strong></TableCell>
                      <TableCell><strong>Classification</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredLoans.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={11} align="center">
                          <Typography color="text.secondary" sx={{ py: 4 }}>
                            No loans found for the selected criteria
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredLoans.map((loan) => (
                        <TableRow key={loan.loanId} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {loan.loanNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>{loan.customerName || 'N/A'}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {loan.customerLocation || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{loan.customerPhoneNumber || loan.customerPhone || 'N/A'}</Typography>
                          </TableCell>
                          <TableCell>
                            {loan.contactPerson ? (
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {loan.contactPerson.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {loan.contactPerson.phone} ({loan.contactPerson.relationship})
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">N/A</Typography>
                            )}
                          </TableCell>
                          <TableCell>{loan.loanOfficerName || 'N/A'}</TableCell>
                          <TableCell align="right">{formatCurrency(loan.principalAmount)}</TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="medium" color="error.main">
                              {formatCurrency(loan.amountOutstanding)}
                            </Typography>
                          </TableCell>
                          <TableCell>{format(new Date(loan.dueDate), 'dd/MM/yyyy')}</TableCell>
                          <TableCell align="right">
                            <Typography
                              fontWeight="medium"
                              color={loan.daysInArrears > 0 ? 'error.main' : 'success.main'}
                            >
                              {loan.daysInArrears}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={loan.classification.label}
                              color={loan.classification.color}
                              size="small"
                              variant="filled"
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Total Summary */}
              {filteredLoans.length > 0 && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="body2">
                      <strong>Total Loans:</strong> {filteredLoans.length}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Outstanding:</strong> {formatCurrency(filteredLoans.reduce((sum, loan) => sum + loan.amountOutstanding, 0))}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Principal:</strong> {formatCurrency(filteredLoans.reduce((sum, loan) => sum + loan.principalAmount, 0))}
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

        </>
      )}
    </Box>
  );
}

// Loan Portfolio Report Tab Component
function LoanPortfolioReportTab({ startDate, endDate, onStartDateChange, onEndDateChange, formatCurrency }: any) {
  const { enqueueSnackbar } = useSnackbar();
  const [statusFilter, setStatusFilter] = useState<LoanPortfolioStatus | ''>('');
  const [classificationFilter, setClassificationFilter] = useState<LoanPortfolioClassification | ''>('');
  const [productFilter, setProductFilter] = useState<string>('');

  // Build query params
  const queryParams: LoanPortfolioQueryParams = useMemo(() => {
    const params: LoanPortfolioQueryParams = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (statusFilter) params.status = statusFilter;
    if (classificationFilter) params.classification = classificationFilter;
    if (productFilter) params.productId = productFilter;
    return params;
  }, [startDate, endDate, statusFilter, classificationFilter, productFilter]);

  const { data, isLoading, error, refetch } = useLoanPortfolioReport(queryParams);
  const { data: loanProducts = [] } = useLoanProducts();
  const exportMutation = useExportLoanPortfolio();

  const handleExportExcel = async () => {
    try {
      await exportMutation.mutateAsync(queryParams);
      enqueueSnackbar('Loan portfolio report exported successfully', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Failed to export report', { variant: 'error' });
    }
  };

  const getClassificationColor = (classification: LoanPortfolioClassification): 'success' | 'info' | 'warning' | 'error' | 'default' => {
    switch (classification) {
      case 'Performing': return 'success';
      case 'Watch': return 'info';
      case 'Substandard': return 'warning';
      case 'Doubtful': return 'error';
      case 'Loss': return 'default';
      default: return 'default';
    }
  };

  return (
    <Box>
      {/* Filter Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Filter Options
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => onStartDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              helperText="Disbursement from"
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => onEndDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              helperText="Disbursement to"
            />
            <TextField
              label="Loan Status"
              select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as LoanPortfolioStatus | '')}
              size="small"
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="OVERDUE">Overdue</MenuItem>
              <MenuItem value="PAID">Paid</MenuItem>
              <MenuItem value="DEFAULTED">Defaulted</MenuItem>
              <MenuItem value="WRITTEN_OFF">Written Off</MenuItem>
            </TextField>
            <TextField
              label="Classification"
              select
              value={classificationFilter}
              onChange={(e) => setClassificationFilter(e.target.value as LoanPortfolioClassification | '')}
              size="small"
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">All Classifications</MenuItem>
              <MenuItem value="Performing">Performing</MenuItem>
              <MenuItem value="Watch">Watch</MenuItem>
              <MenuItem value="Substandard">Substandard</MenuItem>
              <MenuItem value="Doubtful">Doubtful</MenuItem>
              <MenuItem value="Loss">Loss</MenuItem>
            </TextField>
            <TextField
              label="Loan Product"
              select
              value={productFilter}
              onChange={(e) => setProductFilter(e.target.value)}
              size="small"
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All Products</MenuItem>
              {loanProducts.map((product) => (
                <MenuItem key={product.productId} value={product.productId}>
                  {product.productName}
                </MenuItem>
              ))}
            </TextField>
            <Button startIcon={<RefreshIcon />} onClick={() => refetch()} size="small">
              Refresh
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleExportExcel}
              disabled={exportMutation.isPending}
              size="small"
            >
              {exportMutation.isPending ? 'Exporting...' : 'Export Excel'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load loan portfolio report. {error instanceof Error ? error.message : 'Please try again.'}
        </Alert>
      )}

      {data && data.success && (
        <>
          {/* Summary Cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3 }}>
            <MetricCard
              title="Total Records"
              value={data.totalRecords}
              subtitle="Loans in portfolio"
              color="primary"
            />
            <MetricCard
              title="Outstanding Balance"
              value={formatCurrency(data.summary?.totalOutstandingBalance || 0)}
              subtitle="Total principal outstanding"
              color="info"
            />
            <MetricCard
              title="Outstanding Interest"
              value={formatCurrency(data.summary?.totalOutstandingInterest || 0)}
              subtitle="Accrued interest"
              color="warning"
            />
            <MetricCard
              title="Outstanding Penalty"
              value={formatCurrency(data.summary?.totalOutstandingPenalty || 0)}
              subtitle="Accrued penalties"
              color="error"
            />
            <MetricCard
              title="Principal in Arrears"
              value={formatCurrency(data.summary?.totalPrincipalInArrears || 0)}
              subtitle="Overdue principal"
              color="error"
            />
          </Box>

          {/* Classification Breakdown */}
          {data.summary?.classificationBreakdown && (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2, mb: 3 }}>
              <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="caption">Performing</Typography>
                  <Typography variant="h6">{data.summary.classificationBreakdown.performing?.count || 0} loans</Typography>
                  <Typography variant="body2">{formatCurrency(data.summary.classificationBreakdown.performing?.amount || 0)}</Typography>
                </CardContent>
              </Card>
              <Card sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="caption">Watch</Typography>
                  <Typography variant="h6">{data.summary.classificationBreakdown.watch?.count || 0} loans</Typography>
                  <Typography variant="body2">{formatCurrency(data.summary.classificationBreakdown.watch?.amount || 0)}</Typography>
                </CardContent>
              </Card>
              <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="caption">Substandard</Typography>
                  <Typography variant="h6">{data.summary.classificationBreakdown.substandard?.count || 0} loans</Typography>
                  <Typography variant="body2">{formatCurrency(data.summary.classificationBreakdown.substandard?.amount || 0)}</Typography>
                </CardContent>
              </Card>
              <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="caption">Doubtful</Typography>
                  <Typography variant="h6">{data.summary.classificationBreakdown.doubtful?.count || 0} loans</Typography>
                  <Typography variant="body2">{formatCurrency(data.summary.classificationBreakdown.doubtful?.amount || 0)}</Typography>
                </CardContent>
              </Card>
              <Card sx={{ bgcolor: 'grey.400' }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="caption">Loss</Typography>
                  <Typography variant="h6">{data.summary.classificationBreakdown.loss?.count || 0} loans</Typography>
                  <Typography variant="body2">{formatCurrency(data.summary.classificationBreakdown.loss?.amount || 0)}</Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Loan Portfolio Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Loan Portfolio Details ({data.data?.length || 0} loans)
              </Typography>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Loan #</strong></TableCell>
                      <TableCell><strong>Client Name</strong></TableCell>
                      <TableCell><strong>ID Number</strong></TableCell>
                      <TableCell><strong>Loan Type</strong></TableCell>
                      <TableCell><strong>Disbursement</strong></TableCell>
                      <TableCell><strong>Expected Completion</strong></TableCell>
                      <TableCell align="right"><strong>Interest Rate</strong></TableCell>
                      <TableCell><strong>Frequency</strong></TableCell>
                      <TableCell><strong>Employer/Business</strong></TableCell>
                      <TableCell align="right"><strong>Outstanding Balance</strong></TableCell>
                      <TableCell align="right"><strong>Outstanding Interest</strong></TableCell>
                      <TableCell align="right"><strong>Outstanding Penalty</strong></TableCell>
                      <TableCell align="right"><strong>Principal in Arrears</strong></TableCell>
                      <TableCell align="right"><strong>Days in Arrears</strong></TableCell>
                      <TableCell><strong>Classification</strong></TableCell>
                      <TableCell><strong>Email</strong></TableCell>
                      <TableCell><strong>Phone</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(!data.data || data.data.length === 0) ? (
                      <TableRow>
                        <TableCell colSpan={17} align="center">
                          <Typography color="text.secondary" sx={{ py: 4 }}>
                            No loans found for the selected criteria
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.data.map((loan, index) => (
                        <TableRow key={`${loan.loanNumber}-${index}`} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {loan.loanNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>{loan.clientName || 'N/A'}</TableCell>
                          <TableCell>{loan.idNumber || 'N/A'}</TableCell>
                          <TableCell>{loan.loanType || 'N/A'}</TableCell>
                          <TableCell>
                            {loan.disbursementDate ? format(new Date(loan.disbursementDate), 'dd/MM/yyyy') : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {loan.expectedCompletionDate ? format(new Date(loan.expectedCompletionDate), 'dd/MM/yyyy') : 'N/A'}
                          </TableCell>
                          <TableCell align="right">{loan.interestRate ? `${loan.interestRate}%` : 'N/A'}</TableCell>
                          <TableCell>{loan.repaymentFrequency || 'N/A'}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {loan.employerNameOrBusiness || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="medium">
                              {formatCurrency(loan.outstandingBalance || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{formatCurrency(loan.outstandingInterest || 0)}</TableCell>
                          <TableCell align="right">{formatCurrency(loan.outstandingPenalty || 0)}</TableCell>
                          <TableCell align="right">
                            <Typography color={loan.principalInArrears > 0 ? 'error.main' : 'inherit'}>
                              {formatCurrency(loan.principalInArrears || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              fontWeight="medium"
                              color={loan.daysInArrears > 0 ? 'error.main' : 'success.main'}
                            >
                              {loan.daysInArrears || 0}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={loan.classification || 'N/A'}
                              color={getClassificationColor(loan.classification)}
                              size="small"
                              variant="filled"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {loan.email || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>{loan.mobilePhone || 'N/A'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Total Summary */}
              {data.data && data.data.length > 0 && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="body2">
                      <strong>Total Loans:</strong> {data.data.length}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Outstanding:</strong> {formatCurrency(data.data.reduce((sum, loan) => sum + (loan.outstandingBalance || 0), 0))}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Interest:</strong> {formatCurrency(data.data.reduce((sum, loan) => sum + (loan.outstandingInterest || 0), 0))}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Penalties:</strong> {formatCurrency(data.data.reduce((sum, loan) => sum + (loan.outstandingPenalty || 0), 0))}
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {data && !data.success && (
        <Alert severity="warning">
          The report request was not successful. Please check your filters and try again.
        </Alert>
      )}
    </Box>
  );
}

// Helper Components
function MetricCard({ title, value, subtitle, color = 'default' }: any) {
  return (
    <Card>
      <CardContent>
        <Typography variant="caption" color="text.secondary" gutterBottom display="block">
          {title}
        </Typography>
        <Typography variant="h4" fontWeight="bold" color={`${color}.main`}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function MetricRow({ label, value, bold = false }: any) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={bold ? 'bold' : 'regular'}>
        {value}
      </Typography>
    </Box>
  );
}

export default Reports;
